import React, { useState, useEffect } from 'react';
import {
  Expense,
  UpdateExpenseDto,
  ExpenseCategory,
  ExpenseStatus,
  PaymentMethod,
  getExpenseCategoryDisplay,
  getPaymentMethodDisplay,
} from '../../api/expenses';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';
import AmountInput from '../ui/AmountInput';

interface EditExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (data: UpdateExpenseDto) => Promise<void>;
  expense: Expense | null;
}

const EditExpenseModal: React.FC<EditExpenseModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  expense,
}) => {
  const [formData, setFormData] = useState<UpdateExpenseDto>({
    transactionDate: '',
    category: ExpenseCategory.OTHER,
    description: '',
    amount: 0,
    status: ExpenseStatus.PENDING,
    paymentMethod: PaymentMethod.CASH,
    receiptNumber: '',
    vendorId: '',
    notes: '',
  });

  const [loading, setLoading] = useState(false);

  // Initialize form data when expense changes
  useEffect(() => {
    if (expense) {
      setFormData({
        transactionDate: expense.transactionDate.split('T')[0], // Convert to date format
        category: expense.category,
        description: expense.description,
        amount: expense.amount,
        status: expense.status,
        paymentMethod: expense.paymentMethod,
        receiptNumber: expense.receiptNumber || '',
        vendorId: expense.vendorId || '',
        notes: expense.notes || '',
      });
    }
  }, [expense]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading) return;
    
    try {
      setLoading(true);
      
      const submitData: UpdateExpenseDto = {
        ...formData,
        receiptNumber: formData.receiptNumber || undefined,
        vendorId: formData.vendorId || undefined,
        notes: formData.notes || undefined,
      };

      await onUpdate(submitData);
    } catch (error) {
      console.error('Error updating expense:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!expense) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title={`Edit Expense - ${expense.id.slice(0, 8)}`} onClose={onClose} />
      <form id="edit-expense-form" onSubmit={handleSubmit}>
        <BrandModalBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="transactionDate" className="block text-sm font-medium text-slate-700">
                      Transaction Date *
                    </label>
                    <input
                      type="date"
                      id="transactionDate"
                      value={formData.transactionDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, transactionDate: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-slate-700">
                      Category *
                    </label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as ExpenseCategory }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      required
                    >
                      {Object.values(ExpenseCategory).map(category => (
                        <option key={category} value={category}>
                          {getExpenseCategoryDisplay(category)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="amount" className="block text-sm font-medium text-slate-700">
                      Amount (ZAR) *
                    </label>
                    <AmountInput
                      id="amount"
                      value={formData.amount}
                      onChange={(v) => setFormData(prev => ({ ...prev, amount: v ?? 0 }))}
                      min={0}
                      required
                      inputClassName="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-slate-700">
                      Status *
                    </label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as ExpenseStatus }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      required
                    >
                      {Object.values(ExpenseStatus).map(status => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="paymentMethod" className="block text-sm font-medium text-slate-700">
                      Payment Method *
                    </label>
                    <select
                      id="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      required
                    >
                      {Object.values(PaymentMethod).map(method => (
                        <option key={method} value={method}>
                          {getPaymentMethodDisplay(method)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="receiptNumber" className="block text-sm font-medium text-slate-700">
                      Receipt Number
                    </label>
                    <input
                      type="text"
                      id="receiptNumber"
                      value={formData.receiptNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, receiptNumber: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      placeholder="Enter receipt number..."
                    />
                  </div>

                  <div>
                    <label htmlFor="vendorId" className="block text-sm font-medium text-slate-700">
                      Vendor ID
                    </label>
                    <input
                      type="text"
                      id="vendorId"
                      value={formData.vendorId}
                      onChange={(e) => setFormData(prev => ({ ...prev, vendorId: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      placeholder="Enter vendor ID..."
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mt-6">
                <label htmlFor="description" className="block text-sm font-medium text-slate-700">
                  Description *
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder="Describe the expense..."
                  required
                />
              </div>

              {/* Notes */}
              <div className="mt-6">
                <label htmlFor="notes" className="block text-sm font-medium text-slate-700">
                  Notes
                </label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder="Additional notes..."
                />
              </div>

              {/* Amount Display */}
              <div className="mt-4 flex justify-end">
                <div className="text-right">
                  <div className="text-sm text-slate-500">Total Amount</div>
                  <div className="text-xl font-bold text-red-600">
                    {(formData.amount || 0).toLocaleString('en-ZA', { 
                      style: 'currency', 
                      currency: 'ZAR',
                      minimumFractionDigits: 2 
                    })}
                  </div>
                </div>
              </div>
        </BrandModalBody>

        <BrandModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
            Cancel
          </button>
          <button
            type="submit"
            form="edit-expense-form"
            disabled={loading || !formData.description?.trim() || (formData.amount || 0) <= 0}
            className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Updating...' : 'Update Expense'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default EditExpenseModal; 